import type { NavbarMenuListElementProps, NavbarMenuListGroupElementProps } from '@/navbarElement/Navbar.type';
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { cn } from '../../utils/cn';
import * as styles from '../Navbar.css';

const JUSTIFY_CLASS_MAP = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
};

export function NavbarMenuListGroupV2({
  element,
  parent,
  className,
  children,
  style,
  attributes,
}: NavbarMenuListGroupElementProps & {
  parent?: NavbarMenuListElementProps['element'];
}) {
  const {
    type,
    justify,
    customAttributes,
    lineDividerStyle,
    lineDividerWidth,
    lineDividerHeight,
    lineDividerColor,
    mobileGap,
  } = element.attrs || {};

  const showDivider = lineDividerStyle && lineDividerStyle !== 'none';

  const dividerStyle = showDivider
    ? ({
        '--line-divider-width': lineDividerWidth || '0.5px',
        '--line-divider-height': lineDividerHeight || '18px',
        '--line-divider-style': lineDividerStyle || 'solid',
        '--line-divider-color': lineDividerColor || '#000000',
        '--line-divider-gap': '8px',
      } as CSSProperties)
    : {};

  // Handle flex justification for middle type groups
  const flexJustify = type === 'middle' && justify ? JUSTIFY_CLASS_MAP[justify] : '';

  const baseStyle: CSSProperties = {
    '--min-height': 'var(--height)',
    '--mobile-gap': mobileGap,
    ...dividerStyle,
    ...style,
  };

  const isMiddleGroupCentered =
    parent?.content?.[1]?.attrs?.type === 'middle' &&
    parent.content[1].attrs?.justify === 'center' &&
    parent.content.length === 3;

  const [isOverFlowing, setIsOverFlowing] = useState(false);

  const observersRef = useRef<[MutationObserver, ResizeObserver] | null>(null);

  useEffect(() => {
    return () => {
      observersRef.current?.forEach((o) => {
        o.disconnect();
      });
    };
  }, []);

  const handleRef = (r: HTMLDivElement | null) => {
    if (!r) return;

    const checkOverflow = () => {
      if (r.scrollWidth > r.clientWidth) {
        setIsOverFlowing(true);
      } else {
        setIsOverFlowing(false);
      }
    };

    // Initial check
    requestAnimationFrame(checkOverflow);

    // Create mutation observer to watch for changes
    const observer = new MutationObserver(() => {
      requestAnimationFrame(checkOverflow);
    });

    // Observe changes to child elements, attributes, and subtree
    observer.observe(r, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    // Also listen for resize events
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(checkOverflow);
    });
    resizeObserver.observe(r);

    observersRef.current = [observer, resizeObserver];
  };

  const overFlowClasses = cn(
    '!overflow-none !overflow-x-auto',
    (isOverFlowing || (!observersRef.current && (element.content?.length || 0) > 13)) && '!justify-start',
  );

  const flexChildBehaviorClasses = isMiddleGroupCentered
    ? cn(
        overFlowClasses,
        type === 'middle' && 'flex-grow',
        type === 'left' && 'shrink-0 flex-1',
        type === 'right' && 'shrink-0 flex-1',
      )
    : cn(type === 'middle' && 'flex-1', type === 'left' && 'shrink-0', type === 'right' && 'shrink-0');

  const classes = cn(
    flexChildBehaviorClasses,
    styles.navbarMenuListGroupV2,
    type === 'left' && styles.alignLeftV2,
    type === 'middle' &&
      cn(
        justify === 'center' && styles.alignCenterV2,
        justify === 'start' && styles.alignLeftV2,
        justify === 'end' && styles.alignRightV2,
      ),
    type === 'right' && styles.alignRightV2,
    showDivider && 'navbar-divider-group',
    className,
  );

  return (
    <div
      {...attributes}
      {...customAttributes}
      style={baseStyle}
      className={cn(classes, flexJustify)}
      {...(showDivider && { 'data-line-divider': 'true' })}
      ref={handleRef}
    >
      {children}
    </div>
  );
}
